import { AuthResponse } from '@/constants/types/misc/Auth';
import Fetch, { decodeToken, getQueryParams } from './_utils';
import {
  CreateUserType,
  ListUserType,
  UpdateUserType,
  UserAdminRow,
  UserAdminUpdatePayload,
  UserType,
} from '@/constants/types/models/user/User';
import { ListAPIResponseType } from '@/constants/types/misc/api';
import { RoleType } from '@/constants/types/models/role/Role';
import {
  CreateUserClientSourceType,
  UpdateUserClientSourceType,
  UserClientSourceType,
} from '@/constants/types/models/user/UserClientSource';

const saveTemporaryPasswordData = (data: any, email: string) => {
  try {
    localStorage.setItem('session', data.session);
    localStorage.setItem('userEmail', email);
    return true;
  } catch {
    return false;
  }
};

const clearTemporaryPasswordData = () => {
  localStorage.removeItem('session');
  localStorage.removeItem('userEmail');
  return true;
};

const saveForgotPasswordData = (email: string) => {
  try {
    localStorage.setItem('userEmail', email);
    return true;
  } catch {
    return false;
  }
};

export const clearForgotPasswordData = () => {
  localStorage.removeItem('userEmail');
  return true;
};

// TODO: Switch to saving refreshToken in cookies
const saveLoggedInData = (data: any) => {
  try {
    const decodedToken = decodeToken(data.token);
    localStorage.setItem('token', data.token);
    localStorage.setItem('accessToken', data.access_token);
    localStorage.setItem('refreshToken', data.refresh_token);
    localStorage.setItem('userFirstName', decodedToken.name);
    localStorage.setItem('userLastName', decodedToken.family_name);
    return true;
  } catch {
    return false;
  }
};

const saveRefreshTokenData = (data: any) => {
  try {
    localStorage.setItem('token', data.token);
    localStorage.setItem('accessToken', data.access_token);
    return true;
  } catch {
    return false;
  }
};

export const clearLoggedInData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userFirstName');
  localStorage.removeItem('userLastName');
  localStorage.removeItem('admin_schema');
};

export const getUsersService = async (params?: ListUserType) => {
  // Get query params
  const queryParams = params ? getQueryParams(params) : '';
  // Send request
  const response = await Fetch(`users/${queryParams}`, {
    method: 'GET',
    signal: params?.signal,
  });
  if (response.ok) {
    const data = await response.json();
    return data as ListAPIResponseType<UserType>;
  }
  throw new Error('GET_USERS_ERROR');
};

export const loginService = async (params: {
  email: string;
  password: string;
}): Promise<AuthResponse> => {
  // Send request
  const response = await Fetch('users/login/', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  // Get response
  if (response.ok) {
    const data = await response.json();
    // Temporary password case
    if (Object.keys(data).includes('session')) {
      saveTemporaryPasswordData(data, params.email);
      return { status: 'NEW_PASSWORD_REQUIRED' };
      // Logged in case
    } else if (Object.keys(data).includes('token')) {
      saveLoggedInData(data);
      return { status: 'LOGGED_IN' };
    }
  } else {
    // Error case
    try {
      const data = await response.json();
      if (
        data.error &&
        ['INVALID_CREDENTIALS', 'TEMPORARY_PASSWORD_EXPIRED'].includes(
          data.error
        )
      ) {
        return { status: data.error };
      }
    } catch (error) {}
  }
  return { status: 'ERROR' };
};

export const setNewPasswordService = async (params: {
  email?: string;
  password: string;
  session?: string;
}): Promise<AuthResponse> => {
  // Get session
  if (!params.session) {
    params.session = localStorage.getItem('session') || '';
  }
  // Get email
  if (!params.email) {
    params.email = localStorage.getItem('userEmail') || '';
  }
  // Send request
  const response = await Fetch('users/set_permanent_password/', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (response.ok) {
    const data = await response.json();
    // Logged in case
    if (Object.keys(data).includes('token')) {
      saveLoggedInData(data);
      clearTemporaryPasswordData();
      // Return
      return { status: 'LOGGED_IN' };
    }
  }
  return { status: 'ERROR' };
};

// TODO: Switch to saving refreshToken in cookies
export const refreshTokenService = async () => {
  // Get refresh token
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return false;
  }
  // Send request
  const response = await Fetch(
    'users/refresh_token/',
    {
      method: 'POST',
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    },
    true,
    true
  );
  // Get response
  if (response.ok) {
    const data = await response.json();
    // Logged in case
    if (Object.keys(data).includes('token')) {
      saveRefreshTokenData(data);
      // Return
      return true;
    }
  }
  return false;
};

export const forgotPasswordService = async (params: {
  email: string;
}): Promise<AuthResponse> => {
  // Send request
  const response = await Fetch('users/forgot_password/', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  // Get response
  if (response.ok) {
    saveForgotPasswordData(params.email);
    return { status: 'CONFIRMATION_CODE_SENT' };
  } else {
    // Error case
    try {
      const data = await response.json();
      if (
        data.error &&
        ['INVALID_CREDENTIALS', 'LIMIT_EXCEEDED'].includes(data.error)
      ) {
        return { status: data.error };
      }
    } catch (error) {}
  }
  return { status: 'ERROR' };
};

export const confirmForgotPasswordService = async (params: {
  email?: string;
  password: string;
  confirmation_code: string;
}): Promise<AuthResponse> => {
  // Get email
  if (!params.email) {
    params.email = localStorage.getItem('userEmail') || '';
  }
  // Send request
  const response = await Fetch('users/confirm_forgot_password/', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  // Get response
  if (response.ok) {
    return { status: 'NEW_PASSWORD_SET' };
  } else {
    // Error case
    try {
      const data = await response.json();
      if (
        data.error &&
        [
          'INVALID_CREDENTIALS',
          'CONFIRMATION_CODE_EXPIRED',
          'LIMIT_EXCEEDED',
        ].includes(data.error)
      ) {
        return { status: data.error };
      }
    } catch (error) {}
  }
  return { status: 'ERROR' };
};

export const getCurrentUserRolesService = async (params: {
  signal?: AbortSignal;
}) => {
  // Send request
  const response = await Fetch('users/userrole/current_user/', {
    method: 'GET',
    signal: params?.signal,
  });
  // Get response
  if (response.ok) {
    const data = await response.json();
    return data as ListAPIResponseType<RoleType>;
  } else {
    throw new Error('ERROR');
  }
};

export const getUserClientSourceService = async (params: {
  signal?: AbortSignal;
}) => {
  const response = await Fetch('users/user_client_source/', {
    method: 'GET',
    signal: params.signal,
  });
  if (response.ok) {
    const data = await response.json();
    return data as ListAPIResponseType<UserClientSourceType>;
  }
};

export const bulkCreateUserClientSourceService = async (params: {
  userClientSources: CreateUserClientSourceType[];
}) => {
  if (!params.userClientSources.length) return true;
  // Send request
  const response = await Fetch(`users/user_client_source/bulk_create/`, {
    method: 'POST',
    body: JSON.stringify(params.userClientSources),
  });
  // Get response
  if (response.ok) {
    return true;
  }
  throw new Error('Error');
};

export const bulkUpdateUserClientSourceService = async (params: {
  userClientSources: UpdateUserClientSourceType[];
}) => {
  if (!params.userClientSources.length) return true;
  // Send request
  const response = await Fetch(`users/user_client_source/bulk_update/`, {
    method: 'PATCH',
    body: JSON.stringify(params.userClientSources),
  });
  // Get response
  if (response.ok) {
    return true;
  }
  throw new Error('Error');
};

export const bulkDeleteUserClientSourceService = async (params: {
  userClientSourceIds: number[];
}) => {
  if (!params.userClientSourceIds.length) return true;
  // Send request
  const response = await Fetch(`users/user_client_source/bulk_destroy/`, {
    method: 'DELETE',
    body: JSON.stringify(params.userClientSourceIds),
  });
  // Get response
  if (response.ok) {
    return true;
  }
  throw new Error('Error');
};

export const updateUserService = async (params: UpdateUserType) => {
  // Send request
  const response = await Fetch(`users/${params.id}/update/`, {
    method: 'PATCH',
    body: JSON.stringify(params),
  });
  // Get response
  if (response.ok) {
    const data = await response.json();
    return data as UserType;
  }
  throw new Error('Error');
};
export const updateAvailabilityUserService = async (params: UpdateUserType) => {
  // Send request
  const response = await Fetch(`users/${params.id}/availability_update/`, {
    method: 'PATCH',
    body: JSON.stringify(params),
  });
  // Get response
  if (response.ok) {
    const data = await response.json();
    return data as UserType;
  }
  throw new Error('Error');
};

const parseBackendError = async (
  response: Response,
  fallback: string
): Promise<string> => {
  try {
    const data = await response.json();
    if (data?.error) return data.error as string;
    return JSON.stringify(data);
  } catch {
    return fallback;
  }
};

export const createUserService = async (params: CreateUserType) => {
  const response = await Fetch('users/', {
    method: 'POST',
    // Subsidiary-Id header is the backend-recommended way to scope the
    // request to a subsidiary. We also send it in the body as a fallback.
    headers: params.subsidiary
      ? { 'Subsidiary-Id': params.subsidiary }
      : undefined,
    body: JSON.stringify(params),
  });
  if (response.ok) {
    return true;
  }
  throw new Error(await parseBackendError(response, 'CREATE_USER_ERROR'));
};

export const getRolesService = async (params?: { signal?: AbortSignal }) => {
  const response = await Fetch('roles/', {
    method: 'GET',
    signal: params?.signal,
  });
  if (response.ok) {
    const data: ListAPIResponseType<RoleType> = await response.json();
    return data.results;
  }
  throw new Error('GET_ROLES_ERROR');
};

export const assignUserRoleService = async (params: {
  user: string;
  role: number;
}) => {
  const response = await Fetch('users/userrole/', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (response.ok) {
    return true;
  }
  throw new Error(await parseBackendError(response, 'ASSIGN_ROLE_ERROR'));
};

export const getUsersAdminListService = async () => {
  const response = await Fetch('users/admin_list/', { method: 'GET' });
  if (response.ok) {
    return (await response.json()) as UserAdminRow[];
  }
  throw new Error(await parseBackendError(response, 'USERS_ADMIN_LIST_ERROR'));
};

export const updateUserVacationModeService = async (
  id: string,
  isVacationMode: boolean
) => {
  const response = await Fetch(`users/${id}/vacation_mode/`, {
    method: 'PATCH',
    body: JSON.stringify({ is_vacation_mode: isVacationMode }),
  });
  if (response.ok) {
    return (await response.json()) as UserAdminRow;
  }
  throw new Error(
    await parseBackendError(response, 'USER_VACATION_MODE_ERROR')
  );
};

export const updateUserAdminService = async (
  id: string,
  payload: UserAdminUpdatePayload
) => {
  const response = await Fetch(`users/${id}/admin_update/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (response.ok) {
    return (await response.json()) as UserAdminRow;
  }
  throw new Error(await parseBackendError(response, 'USER_ADMIN_UPDATE_ERROR'));
};
